import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/use-auth-compat';
import {
  fetchSubscriptionPlans,
  fetchSubscriptionStatus,
  createSubscriptionOrder,
  verifySubscriptionPayment,
  createTierChangeOrder,
  verifyTierChangePayment,
  scheduleTierDowngrade,
  cancelPendingTierChange,
  cancelSubscription,
  turnOffAutoRenew,
  createRenewOrder,
  verifyRenewPayment,
  createScheduledOrder,
  verifyScheduledPayment,
  cancelScheduledSubscription,
  fetchSubscriptionHistory,
  fetchAdBoostPackages,
  fetchAdBoostStatus,
  createAdBoostOrder,
  verifyAdBoostPayment,
  updateAdBoostAutoRenew,
  type BillingCycle,
  type CurrentSubscriptionResponse,
  type AdBoostStatusResponse,
  type SubscriptionStatusResponse,
  type TeacherSubscriptionHistoryResponse,
} from '@/services/teacherSubscriptionService';
import { formatTrialDate } from '@/lib/trialDate';

declare global {
  interface Window {
    Razorpay: any;
  }
}

// ---------------------------------------------------------------------------
// Granularity note for F3 (subscribe/upgrade page) and F5 (ad-boost page):
//
// Every endpoint that is a plain read or a plain state change (no payment) gets
// its own small, single-purpose hook — useSubscriptionPlans, useSubscriptionStatus,
// useScheduleTierDowngrade, useCancelPendingTierChange, useCancelSubscription,
// useAdBoostPackages, useAdBoostStatus, useUpdateAdBoostAutoRenew — call these
// directly, they map 1:1 onto a service function.
//
// Every flow that involves Razorpay checkout is instead exposed as ONE combined
// mutation that does order-create → open-checkout → verify end to end, mirroring
// usePurchaseCoins in use-coin-purchase.ts exactly:
//   - useSubscribeToTier()  — POST /order (subscription_id flow) → checkout → POST /verify
//   - useUpgradeTier()      — POST /tier-change/order (order_id flow) → checkout → POST /tier-change/verify
//   - usePurchaseAdBoost()  — POST /order (order_id flow) → checkout → POST /verify
// This keeps the "create order, open Razorpay, verify" sequence atomic and
// un-skippable from the caller's side (same reasoning as the coin-purchase
// hook) — a page component only ever sees a single `.mutate(...)` call and a
// single pending/success/error state, never a dangling order it forgot to verify.
// If a later task needs the order-create step in isolation (e.g. to show order
// terms before the user commits to opening checkout), split these back out —
// the underlying service functions are already exported individually.
// ---------------------------------------------------------------------------

/** Shared Razorpay checkout launcher for both the order_id and subscription_id
 * variants. Razorpay's checkout.js accepts either order_id (+ amount + currency)
 * for a one-time payment, or subscription_id for a recurring mandate — never
 * both. See Razorpay's checkout options reference. */
interface RazorpayCheckoutResult {
  razorpayPaymentId: string;
  razorpayOrderId?: string;
  razorpaySubscriptionId?: string;
  razorpaySignature: string;
}

function openRazorpayCheckout(options: {
  keyId: string;
  currency: string;
  name: string;
  description: string;
  /**
   * Not currently supplied by any caller. `useAuth().user` is `{ access_token }` only -- it carries
   * no email -- so the four call sites here used to read `auth.user?.email`, which was always
   * undefined at runtime and never prefilled anything. The dead reads are gone; wiring a real
   * prefill needs an actual source for the teacher's email first.
   */
  prefillEmail?: string;
  themeColor?: string;
  payment:
    | { type: 'order'; orderId: string; amountMinor: number }
    | { type: 'subscription'; subscriptionId: string };
}): Promise<RazorpayCheckoutResult> {
  if (typeof window.Razorpay !== 'function') {
    return Promise.reject(new Error('Payment checkout failed to load. Please refresh and try again.'));
  }

  const paymentFields =
    options.payment.type === 'subscription'
      ? { subscription_id: options.payment.subscriptionId }
      : {
          order_id: options.payment.orderId,
          amount: options.payment.amountMinor,
          currency: options.currency,
        };

  return new Promise<RazorpayCheckoutResult>((resolve, reject) => {
    const razorpay = new window.Razorpay({
      key: options.keyId,
      ...paymentFields,
      name: options.name,
      description: options.description,
      // Square 256×256 logo — Razorpay's recommended size; deployed to /public
      image: 'https://www.bookmycoaching.com/logo_256x256.png',
      prefill: {
        email: options.prefillEmail,
      },
      theme: {
        color: options.themeColor ?? '#1a4066', // teacher navy — matches use-coin-purchase.ts's teacher accent
        backdrop_color: 'rgba(15, 23, 42, 0.7)',
      },
      handler: (response: {
        razorpay_payment_id: string;
        razorpay_order_id?: string;
        razorpay_subscription_id?: string;
        razorpay_signature: string;
      }) => {
        resolve({
          razorpayPaymentId: response.razorpay_payment_id,
          razorpayOrderId: response.razorpay_order_id,
          razorpaySubscriptionId: response.razorpay_subscription_id,
          razorpaySignature: response.razorpay_signature,
        });
      },
      modal: {
        ondismiss: () => reject(new Error('Checkout closed before payment completed')),
      },
    });
    razorpay.open();
  });
}

function useAccessToken(): string {
  const auth = useAuth();
  return auth.user?.access_token ?? '';
}

// =====================================================================
// Subscription — queries
// =====================================================================

export function useSubscriptionPlans() {
  const auth = useAuth();
  const token = useAccessToken();
  return useQuery({
    queryKey: ['teacherSubscriptionPlans'],
    queryFn: () => fetchSubscriptionPlans(token),
    enabled: auth.isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * `enabled` lets a caller that only needs this conditionally -- TrialCountdownBanner, which reads
 * `scheduledSubscription` and only once entitlements say a trial is running -- skip the request
 * entirely rather than adding a GET to every teacher dashboard load. Defaults to true, so the
 * existing call sites are unchanged.
 */
export function useSubscriptionStatus(enabled: boolean = true) {
  const auth = useAuth();
  const token = useAccessToken();
  return useQuery({
    queryKey: ['teacherSubscriptionStatus'],
    queryFn: () => fetchSubscriptionStatus(token),
    enabled: auth.isAuthenticated && enabled,
  });
}

// =====================================================================
// Subscription — mutations
// =====================================================================

interface SubscribeToTierArgs {
  planId: string;
  billingCycle: BillingCycle;
  /**
   * The teacher's explicit auto-renew consent (ADR-151), collected by the checkout consent dialog.
   * Left UNDEFINED by the flag-off caller so the field is omitted from the request body entirely
   * and the backend keeps creating a mandate exactly as it does today.
   */
  autoRenew?: boolean;
}

/**
 * Full initial-subscribe flow: create the order server-side, open checkout against whichever
 * instrument came back, then verify the payment signature server-side. See the granularity note
 * above for why this is one combined mutation rather than separate order/verify hooks.
 *
 * ADR-152: the choice is a choice of INSTRUMENT. `autoRenew: true` (or an omitted field, with the
 * flag off) comes back as a Razorpay Subscription mandate; `autoRenew: false` comes back as a
 * one-time Order, which creates no standing authority to charge. Both are verified through the
 * same POST /verify, which accepts exactly one of the two ids.
 */
export function useSubscribeToTier() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation<CurrentSubscriptionResponse, Error, SubscribeToTierArgs>({
    mutationFn: async ({ planId, billingCycle, autoRenew }) => {
      const token = await auth.getToken();
      // `autoRenew: undefined` is dropped by JSON.stringify, so the flag-off caller sends the
      // exact body it sends today.
      const order = await createSubscriptionOrder(token, { planId, billingCycle, autoRenew });
      const description = `${planId} subscription (${billingCycle.toLowerCase()})`;

      // Mandate-backed: today's path, unchanged.
      if (order.razorpaySubscriptionId) {
        const result = await openRazorpayCheckout({
          keyId: order.razorpayKeyId,
          currency: order.currency,
          name: 'BookMyCoaching',
          description,
          payment: { type: 'subscription', subscriptionId: order.razorpaySubscriptionId },
        });

        return verifySubscriptionPayment(token, {
          planId,
          billingCycle,
          currency: order.currency,
          razorpaySubscriptionId: result.razorpaySubscriptionId ?? order.razorpaySubscriptionId,
          razorpayPaymentId: result.razorpayPaymentId,
          razorpaySignature: result.razorpaySignature,
        });
      }

      // Order-backed: a non-renewing purchase (ADR-152). Structurally identical to the shipped
      // useRenewSubscription below -- a one-time Order, no mandate is ever created.
      if (!order.razorpayOrderId) {
        // Contract violation -- POST /order must return exactly one of the two instruments.
        throw new Error('Subscription order is missing a Razorpay order id');
      }

      const result = await openRazorpayCheckout({
        keyId: order.razorpayKeyId,
        currency: order.currency,
        name: 'BookMyCoaching',
        description,
        payment: { type: 'order', orderId: order.razorpayOrderId, amountMinor: order.amountMinor },
      });

      return verifySubscriptionPayment(token, {
        planId,
        billingCycle,
        currency: order.currency,
        razorpayOrderId: result.razorpayOrderId ?? order.razorpayOrderId,
        razorpayPaymentId: result.razorpayPaymentId,
        razorpaySignature: result.razorpaySignature,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacherSubscriptionStatus'] });
      toast.success('Subscription activated');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Subscription failed');
    },
  });
}

/**
 * Full tier-upgrade flow: create the one-time Order for the price delta,
 * open checkout against its order_id, then verify. Upgrades are billed as a
 * one-time Order (never a new Subscription) — see CreateSubscriptionOrderResponse.
 */
export function useUpgradeTier() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation<CurrentSubscriptionResponse, Error, { targetPlanId: string }>({
    mutationFn: async ({ targetPlanId }) => {
      const token = await auth.getToken();
      const order = await createTierChangeOrder(token, { targetPlanId });

      if (!order.razorpayOrderId) {
        throw new Error('Tier-change order is missing a Razorpay order id');
      }

      const result = await openRazorpayCheckout({
        keyId: order.razorpayKeyId,
        currency: order.currency,
        name: 'BookMyCoaching',
        description: 'Subscription tier upgrade',
        payment: { type: 'order', orderId: order.razorpayOrderId, amountMinor: order.amountMinor },
      });

      return verifyTierChangePayment(token, {
        targetPlanId,
        razorpayOrderId: result.razorpayOrderId ?? order.razorpayOrderId,
        razorpayPaymentId: result.razorpayPaymentId,
        razorpaySignature: result.razorpaySignature,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacherSubscriptionStatus'] });
      toast.success('Subscription upgraded');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Upgrade failed');
    },
  });
}

/** Schedules a downgrade for end-of-period — no payment, so no checkout involved. */
export function useScheduleTierDowngrade() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation<CurrentSubscriptionResponse, Error, { targetPlanId: string }>({
    mutationFn: async ({ targetPlanId }) => {
      const token = await auth.getToken();
      return scheduleTierDowngrade(token, { targetPlanId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacherSubscriptionStatus'] });
      toast.success('Downgrade scheduled for the end of your billing period');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to schedule downgrade');
    },
  });
}

export function useCancelPendingTierChange() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation<CurrentSubscriptionResponse, Error, void>({
    mutationFn: async () => {
      const token = await auth.getToken();
      return cancelPendingTierChange(token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacherSubscriptionStatus'] });
      toast.success('Pending change cancelled');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel pending change');
    },
  });
}

export function useCancelSubscription() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation<CurrentSubscriptionResponse, Error, void>({
    mutationFn: async () => {
      const token = await auth.getToken();
      return cancelSubscription(token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacherSubscriptionStatus'] });
      toast.success('Subscription will end at the close of your current billing period');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel subscription');
    },
  });
}

/**
 * Turn auto-renew OFF (AC#9-13). Copies useCancelSubscription exactly, and is deliberately the
 * ONLY auto-renew mutation: ADR-152 makes the turn-off one-way, so there is no `useTurnOnAutoRenew`
 * and no `{ autoRenew: boolean }` argument that could be read as a settable toggle.
 *
 * Invalidates the history query as well as the status query: the backend records an
 * AUTO_RENEW_DISABLED event, which the History tab must show without a reload. NOT optimistic --
 * a failed turn-off leaves the mandate live, so the card must keep reading "Renews {date}".
 */
export function useTurnOffAutoRenew() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation<CurrentSubscriptionResponse, Error, void>({
    mutationFn: async () => {
      const token = await auth.getToken();
      return turnOffAutoRenew(token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacherSubscriptionStatus'] });
      queryClient.invalidateQueries({ queryKey: ['teacherSubscriptionHistory'] });
      toast.success('Auto-renew is off. You keep your benefits until the end of this period.');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to turn off auto-renew');
    },
  });
}

/**
 * Full manual-renew flow (F14): create the one-time renewal Order, open
 * checkout against its order_id, then verify -- extends the existing
 * subscription's period rather than starting a new one. Mirrors
 * useUpgradeTier's order_id pattern exactly (renew is billed as a one-time
 * Order, same as an upgrade -- never a new Razorpay Subscription).
 */
export function useRenewSubscription() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation<CurrentSubscriptionResponse, Error, void>({
    mutationFn: async () => {
      const token = await auth.getToken();
      const order = await createRenewOrder(token);

      if (!order.razorpayOrderId) {
        throw new Error('Renewal order is missing a Razorpay order id');
      }

      const result = await openRazorpayCheckout({
        keyId: order.razorpayKeyId,
        currency: order.currency,
        name: 'BookMyCoaching',
        description: 'Subscription renewal',
        payment: { type: 'order', orderId: order.razorpayOrderId, amountMinor: order.amountMinor },
      });

      return verifyRenewPayment(token, {
        razorpayOrderId: result.razorpayOrderId ?? order.razorpayOrderId,
        razorpayPaymentId: result.razorpayPaymentId,
        razorpaySignature: result.razorpaySignature,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacherSubscriptionStatus'] });
      queryClient.invalidateQueries({ queryKey: ['teacherSubscriptionHistory'] });
      toast.success('Subscription renewed');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Renewal failed');
    },
  });
}

// =====================================================================
// Scheduled purchase during a trial (F-04)
// =====================================================================

interface ScheduleAtTrialEndArgs {
  planId: string;
  billingCycle: BillingCycle;
}

/**
 * Buy a tier while on the Platinum trial: it is paid for now and starts the day the trial ends.
 *
 * Mirrors `useSubscribeToTier` exactly -- order -> checkout -> verify as one mutation -- against
 * the /scheduled endpoints, which are the only ones that work during a trial (the plain
 * /subscription/order 409s with SUBSCRIPTION_SCHEDULING_REQUIRED). Also invalidates
 * `teacherEntitlements`: scheduling does not change today's entitlements, but the trial block the
 * trial UI gates on is carried on that response.
 */
export function useScheduleSubscriptionAtTrialEnd() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation<CurrentSubscriptionResponse, Error, ScheduleAtTrialEndArgs>({
    mutationFn: async ({ planId, billingCycle }) => {
      const token = await auth.getToken();
      const order = await createScheduledOrder(token, { planId, billingCycle });

      if (!order.razorpaySubscriptionId) {
        // Contract violation -- a scheduled purchase is always a Razorpay Subscription mandate.
        throw new Error('Scheduled subscription order is missing a Razorpay subscription id');
      }

      const result = await openRazorpayCheckout({
        keyId: order.razorpayKeyId,
        currency: order.currency,
        name: 'BookMyCoaching',
        description: `${planId} subscription, starting ${formatTrialDate(order.firstChargeOn)}`,
        payment: { type: 'subscription', subscriptionId: order.razorpaySubscriptionId },
      });

      return verifyScheduledPayment(token, {
        planId,
        billingCycle,
        currency: order.currency,
        razorpaySubscriptionId: result.razorpaySubscriptionId ?? order.razorpaySubscriptionId,
        razorpayPaymentId: result.razorpayPaymentId,
        razorpaySignature: result.razorpaySignature,
      });
    },
    onSuccess: (subscription) => {
      queryClient.invalidateQueries({ queryKey: ['teacherSubscriptionStatus'] });
      queryClient.invalidateQueries({ queryKey: ['teacherEntitlements'] });
      toast.success(`${subscription.tier} starts ${formatTrialDate(subscription.startDate)}, when your trial ends`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Could not schedule your subscription');
    },
  });
}

interface CancelScheduledSubscriptionArgs {
  /**
   * `trial.endsAt` as the server reported it, so the confirmation can name the date the teacher
   * drops to the free plan. Passed in rather than read here: the hook has no query of its own, and
   * the status response is already in the caller's hands.
   */
  trialEndsAt: string | null;
}

/** Cancels a not-yet-started scheduled purchase. The trial keeps running to its own end date. */
export function useCancelScheduledSubscription() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation<SubscriptionStatusResponse, Error, CancelScheduledSubscriptionArgs>({
    mutationFn: async () => {
      const token = await auth.getToken();
      return cancelScheduledSubscription(token);
    },
    onSuccess: (_data, { trialEndsAt }) => {
      queryClient.invalidateQueries({ queryKey: ['teacherSubscriptionStatus'] });
      queryClient.invalidateQueries({ queryKey: ['teacherEntitlements'] });
      const expiry = trialEndsAt ? ` Your trial expires on ${formatTrialDate(trialEndsAt)}.` : '';
      toast.success(`Scheduled subscription cancelled.${expiry}`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel the scheduled subscription');
    },
  });
}

// =====================================================================
// History — queries (F13)
// =====================================================================

/** Paged subscription/ad-boost lifecycle history: server-derived periods[] plus the raw paged events[] stream. */
export function useSubscriptionHistory(page: number, size: number) {
  const auth = useAuth();
  const token = useAccessToken();
  return useQuery<TeacherSubscriptionHistoryResponse>({
    queryKey: ['teacherSubscriptionHistory', page, size],
    queryFn: () => fetchSubscriptionHistory(token, page, size),
    enabled: auth.isAuthenticated,
  });
}

// =====================================================================
// Ad boost — queries
// =====================================================================

export function useAdBoostPackages() {
  const auth = useAuth();
  const token = useAccessToken();
  return useQuery({
    queryKey: ['teacherAdBoostPackages'],
    queryFn: () => fetchAdBoostPackages(token),
    enabled: auth.isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdBoostStatus() {
  const auth = useAuth();
  const token = useAccessToken();
  return useQuery({
    queryKey: ['teacherAdBoostStatus'],
    queryFn: () => fetchAdBoostStatus(token),
    enabled: auth.isAuthenticated,
  });
}

// =====================================================================
// Ad boost — mutations
// =====================================================================

interface PurchaseAdBoostArgs {
  packageId: string;
  autoRenew: boolean;
}

/**
 * Full ad-boost purchase flow: create the one-time Order, open checkout
 * against its order_id, then verify. Always order_id-based — ad-boost has no
 * subscription_id variant (unlike the initial tier-subscribe flow above).
 */
export function usePurchaseAdBoost() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation<AdBoostStatusResponse, Error, PurchaseAdBoostArgs>({
    mutationFn: async ({ packageId, autoRenew }) => {
      const token = await auth.getToken();
      const order = await createAdBoostOrder(token, { packageId, autoRenew });

      const result = await openRazorpayCheckout({
        keyId: order.razorpayKeyId,
        currency: order.currency,
        name: 'BookMyCoaching',
        description: 'Profile ad boost',
        payment: { type: 'order', orderId: order.razorpayOrderId, amountMinor: order.amountMinor },
      });

      return verifyAdBoostPayment(token, {
        packageId,
        currency: order.currency,
        autoRenew,
        razorpayOrderId: result.razorpayOrderId ?? order.razorpayOrderId,
        razorpayPaymentId: result.razorpayPaymentId,
        razorpaySignature: result.razorpaySignature,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacherAdBoostStatus'] });
      toast.success('Ad boost activated');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Ad boost purchase failed');
    },
  });
}

export function useUpdateAdBoostAutoRenew() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  return useMutation<AdBoostStatusResponse, Error, { autoRenew: boolean }>({
    mutationFn: async ({ autoRenew }) => {
      const token = await auth.getToken();
      return updateAdBoostAutoRenew(token, { autoRenew });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teacherAdBoostStatus'] });
      toast.success('Ad boost auto-renew updated');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to update auto-renew');
    },
  });
}
